import { dayjs } from '@tnn/core'
import type { WaybillStatus } from './waybill'

/**
 * Where a waybill is, worked out from its scans (`waybill_event`). Goods are big and unboxed, so the paper bill travels
 * with them and is scanned once per move; a bill always moves whole: onto a trip (`load`), off it at a branch
 * (`unload`), onto a delivery run (`out`), taken back off the run (`back`), handed over (`delivered`) or brought back
 * undelivered (`failed`). A handover recorded by mistake is undone (`undelivered`): the bill is where it was before it.
 * Sent back to the sender (`returned`, at the branch it stood at: the goods go on as a new return bill) it moves no
 * more, unless that return bill is cancelled (`unreturned`). The pieces counted at each scan are a check only: fewer
 * than the bill's are counted short.
 * Pure: the API stores the outcome on the bill after every scan (`status`, `currentBranchId`), the waybill page draws
 * the same.
 */
export const trackKinds = ['load', 'unload', 'out', 'back', 'delivered', 'undelivered', 'failed', 'returned', 'unreturned'] as const
export type TrackKind = (typeof trackKinds)[number]

/** Why a door delivery did not happen: the bill goes back to the branch for another run (or why it is sent back) */
export const deliveryFailReasons = ['no_one', 'unreachable', 'refused', 'wrong_address', 'closed', 'other'] as const
export type DeliveryFailReason = (typeof deliveryFailReasons)[number]

export interface TrackEvent {
  kind: TrackKind
  /** Where it happened: the branch loaded at, unloaded at, or the run's branch */
  branchId?: string | null
  tripId?: string | null
  runId?: string | null
}

/** Where a bill is: standing at a branch, on a trip, out on a run, or handed over */
export type TrackPlace = { kind: 'branch' | 'trip' | 'run'; id: string } | { kind: 'delivered'; id: string | null }

/**
 * The scans that still count: a handover undone (`undelivered`) takes itself and that handover away, so the bill is
 * where it was before it — out on its run again, or standing at the branch
 */
export function standingScans<E extends TrackEvent>(events: readonly E[]): E[] {
  const kept: E[] = []
  for (const event of events) {
    if (event.kind !== 'undelivered') kept.push(event)
    else if (kept.at(-1)?.kind === 'delivered') kept.pop()
  }
  return kept
}

/** The handover a bill stands handed over by, to undo: its last scan that still counts, when that is one */
export function lastHandover<E extends TrackEvent>(events: readonly E[]): E | undefined {
  const last = standingScans(events).at(-1)
  return last?.kind === 'delivered' ? last : undefined
}

/**
 * Who may undo a handover recorded by mistake (ยกเลิกส่งมอบ): whoever may `undeliver` everywhere (`reach` all), a branch
 * lead at the branch it was handed over at (`undeliver@branch`, their home branch), and whoever recorded it, the same
 * day (`today`, YYYY-MM-DD)
 */
export function mayUndeliver(
  handover: { userId?: string | null; branchId?: string | null; at: Date | string },
  me: { userId: string; branchId?: string | null },
  reach: 'all' | 'branch' | undefined,
  today: string,
) {
  if (reach === 'all') return true
  if (reach === 'branch' && me.branchId && me.branchId === handover.branchId) return true
  return !!handover.userId && handover.userId === me.userId && dayjs(handover.at).format('YYYY-MM-DD') === today
}

/** Where its last move put it; at its origin before any */
export function placeOf(bill: { originBranchId: string }, events: readonly TrackEvent[]): TrackPlace {
  const last = standingScans(events).at(-1)
  if (!last) return { kind: 'branch', id: bill.originBranchId }
  if (last.kind === 'load') return { kind: 'trip', id: String(last.tripId) }
  if (last.kind === 'out') return { kind: 'run', id: String(last.runId) }
  if (last.kind === 'delivered') return { kind: 'delivered', id: last.branchId ?? null }
  return { kind: 'branch', id: String(last.branchId) }
}

export interface TrackBill {
  originBranchId: string
  destBranchId: string
  destMode: string
}

export interface Track {
  status: WaybillStatus
  currentBranchId: string | null
  place: TrackPlace
}

/**
 * How a scanned bill stands: handed over (`delivered`); sent back (`returned`); out on a run (`out_for_delivery`); on a
 * truck (`in_transit`); at a branch — still at the origin it never left (`received`), waiting at its destination for the receiver
 * (`ready_for_pickup`) or anywhere else (`at_branch`). `currentBranchId`: the branch it is at, or was last scanned at.
 */
export function trackOf(bill: TrackBill, events: readonly TrackEvent[]): Track {
  const scans = standingScans(events)
  const place = placeOf(bill, scans)
  const last = scans.at(-1)?.branchId ?? bill.originBranchId
  // Out of the origin once it came off a truck elsewhere or went out on a run
  const left = scans.some((e) => e.kind === 'out' || (e.kind === 'unload' && e.branchId !== bill.originBranchId))
  if (place.kind === 'delivered') return { status: 'delivered', currentBranchId: last, place }
  if (scans.at(-1)?.kind === 'returned') return { status: 'returned', currentBranchId: last, place }
  if (place.kind === 'run') return { status: 'out_for_delivery', currentBranchId: last, place }
  if (place.kind === 'trip') return { status: 'in_transit', currentBranchId: last, place }
  const status: WaybillStatus =
    place.id === bill.originBranchId && !left
      ? 'received'
      : place.id === bill.destBranchId && bill.destMode === 'branch'
        ? 'ready_for_pickup'
        : 'at_branch'
  return { status, currentBranchId: place.id, place }
}

/** Fewer pieces counted than the bill has: how many short (none when the count is all there) */
export const shortOf = (pieces: number, counted: number) => Math.max((Number(pieces) || 0) - (Number(counted) || 0), 0)

/** A trip as the drop rules read it: where it loads first and its stops in order */
export interface TripPath {
  originBranchId: string
  stops: readonly string[]
}

/** The position of a branch on a trip: -1 its origin, else its stop's index (undefined: not on it) */
export function legOf(trip: TripPath, branchId: string | null | undefined): number | undefined {
  if (!branchId) return undefined
  if (branchId === trip.originBranchId) return -1
  const i = trip.stops.indexOf(branchId)
  return i < 0 ? undefined : i
}

/**
 * Where a bill loaded at `loadedAt` should come off the trip: its destination when the trip stops there later, else the
 * later stop furthest along the bill's planned route, else the trip's last stop (the plan says nothing better)
 */
export function dropStop(trip: TripPath, bill: { destBranchId: string; route?: readonly string[] | null }, loadedAt?: string | null) {
  const after = trip.stops.slice((legOf(trip, loadedAt) ?? -1) + 1)
  if (after.includes(bill.destBranchId)) return bill.destBranchId
  const route = bill.route ?? []
  const onRoute = after.filter((stop) => route.includes(stop)).sort((a, b) => route.indexOf(b) - route.indexOf(a))
  return onRoute[0] ?? after.at(-1) ?? trip.stops.at(-1)
}

/**
 * Whether a bill leaves its planned route at a branch: its route is known, and the branch is neither on it nor its
 * destination. Off-route moves are allowed (the route is only a plan) but said
 */
export function offRoute(bill: { destBranchId: string; route?: readonly string[] | null }, branchId: string) {
  return !!bill.route?.length && branchId !== bill.destBranchId && !bill.route.includes(branchId)
}
